import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { ArrowUpRight, BookOpen, ChevronLeft, ChevronRight, Database, RefreshCw, Search, TrendingUp } from 'lucide-react';
import { fetchHongguoRankings, refreshHongguoRankings, startHongguoRankingAnalysis, fetchHongguoRankingAnalysis } from '../../../services/adminService';
import { ADMIN_ACCENT, EmptyBlock, ErrorNotice, PrimaryButton, SecondaryButton, StatusBadge } from '../adminUi.jsx';
import { Modal } from '../../ui/Modal';
import HongguoRankingCover from './HongguoRankingCover';
import HongguoAnalysisModal from './HongguoAnalysisModal';
import { createHongguoAnalysisController } from './hongguoAnalysisUtils';
import {
  HONGGUO_BOARDS, mergeRankingSnapshot, normalizeRankingSnapshot, rankingCooldownSeconds,
  rankingMetric, rankingPageKey, rankingSourceUrl, rankingTime, searchRankingPage,
} from './hongguoRankingsUtils';
import './HongguoRankingsTab.css';

const EMPTY_ROWS = [];
const STATUS_LABELS = { EMPTY: '尚未保存', READY: '数据已保存', REFRESHING: '整榜同步中', FAILED: '同步失败', COOLDOWN: '同步冷却中' };

const replaceBoardEntries = (entries, board, key, entry) => ({
  ...Object.fromEntries(Object.entries(entries).filter(([entryKey]) => !entryKey.startsWith(`${board}:`))),
  [key]: entry,
});

const HongguoRankingsTab = () => {
  const [selection, setSelection] = useState({ board: 'hot-drama', page: 1 });
  const [entries, setEntries] = useState({});
  const [boards, setBoards] = useState({});
  const [query, setQuery] = useState('');
  const [coverPreview, setCoverPreview] = useState(null);
  const [analysis] = useState(() => createHongguoAnalysisController({ startJob: startHongguoRankingAnalysis, fetchJob: fetchHongguoRankingAnalysis }));
  const analysisState = useSyncExternalStore(analysis.subscribe, analysis.getSnapshot, analysis.getSnapshot);
  const [now, setNow] = useState(Date.now);
  const mounted = useRef(false);
  const requests = useRef(new Map());
  const refreshRequests = useRef(new Map());
  const boardControls = useRef(new Map());
  const readSequence = useRef(0);
  const selectedPage = useRef(selection);
  const { board, page } = selection;
  const key = rankingPageKey(board, page);
  const entry = entries[key];
  const snapshot = entry?.snapshot;
  const boardState = boards[board];
  const items = snapshot?.items || EMPTY_ROWS;
  const visibleItems = searchRankingPage(items, query);
  const boardLabel = HONGGUO_BOARDS.find(item => item.key === board)?.label;
  const serverCooldown = rankingCooldownSeconds(boardState?.nextRefreshAt, now);
  const localCooldown = rankingCooldownSeconds(boardState?.localNextRefreshAt, now);
  const cooldown = Math.max(serverCooldown, localCooldown);
  const hasCooldown = cooldown > 0;
  const totalPages = snapshot?.totalPages || 5;
  const status = boardState?.status || snapshot?.status;
  const refreshing = boardState?.busy || status === 'REFRESHING';
  const error = entry?.error || boardState?.error;

  useEffect(() => () => analysis.dispose(), [analysis]);

  const updateBoard = useCallback((requestedBoard, patch) => {
    const next = { ...boardControls.current.get(requestedBoard), ...patch };
    boardControls.current.set(requestedBoard, next);
    setBoards(current => ({ ...current, [requestedBoard]: next }));
  }, []);

  const abortBoardReads = useCallback((requestedBoard, exceptKey) => {
    const canceled = [];
    requests.current.forEach((controller, requestedKey) => {
      if (requestedKey.startsWith(`${requestedBoard}:`) && requestedKey !== exceptKey) {
        controller.abort();
        requests.current.delete(requestedKey);
        canceled.push(requestedKey);
      }
    });
    if (canceled.length) setEntries(current => {
      const next = { ...current };
      canceled.forEach(requestedKey => { if (next[requestedKey]) next[requestedKey] = { ...next[requestedKey], busy: '' }; });
      return next;
    });
  }, []);

  const loadPage = useCallback(async function readPage(requestedBoard, requestedPage) {
    const requestedKey = rankingPageKey(requestedBoard, requestedPage);
    // Deduplicate saved-data reads. Controllers also reject late responses after a board replacement.
    if (requests.current.has(requestedKey)) return;
    const controller = new AbortController();
    const readId = ++readSequence.current;
    requests.current.set(requestedKey, controller);
    setEntries(current => ({ ...current, [requestedKey]: {
      ...current[requestedKey], busy: 'read', error: '',
    } }));
    try {
      const data = await fetchHongguoRankings(requestedBoard, requestedPage, { signal: controller.signal });
      if (!mounted.current || controller.signal.aborted || requests.current.get(requestedKey) !== controller) return;
      const incoming = normalizeRankingSnapshot(data, requestedBoard, requestedPage);
      const previousBoard = boardControls.current.get(requestedBoard);
      const canUpdateBoard = !refreshRequests.current.has(requestedBoard) && readId >= (previousBoard?.lastReadId || 0);
      // A manual read can discover a completed sync from another admin or an uncertain POST.
      const newSavedVersion = canUpdateBoard && incoming.status === 'READY' && previousBoard?.fetchedAt
        && new Date(incoming.fetchedAt).getTime() > new Date(previousBoard.fetchedAt).getTime();
      if (newSavedVersion) abortBoardReads(requestedBoard, requestedKey);
      setEntries(current => {
        const next = { ...current[requestedKey], snapshot: mergeRankingSnapshot(current[requestedKey]?.snapshot, incoming), error: '' };
        return newSavedVersion ? replaceBoardEntries(current, requestedBoard, requestedKey, next) : { ...current, [requestedKey]: next };
      });
      if (canUpdateBoard) updateBoard(requestedBoard, {
        status: incoming.status, nextRefreshAt: incoming.nextRefreshAt,
        fetchedAt: incoming.fetchedAt || previousBoard?.fetchedAt, warning: incoming.warning, error: '', lastReadId: readId,
      });
      const active = selectedPage.current;
      if (newSavedVersion && active.board === requestedBoard && active.page !== requestedPage) readPage(active.board, active.page);
    } catch (cause) {
      if (mounted.current && !controller.signal.aborted && requests.current.get(requestedKey) === controller) {
        setEntries(current => ({ ...current, [requestedKey]: {
          ...current[requestedKey], error: `${cause?.message || '榜单读取失败。'} 已保存的数据不会清空，可稍后重新读取。`,
        } }));
      }
    } finally {
      if (requests.current.get(requestedKey) === controller) {
        requests.current.delete(requestedKey);
        if (mounted.current) setEntries(current => ({ ...current, [requestedKey]: { ...current[requestedKey], busy: '' } }));
      }
    }
  }, [abortBoardReads, updateBoard]);

  useEffect(() => {
    mounted.current = true;
    const activeRequests = requests.current;
    const activeRefreshes = refreshRequests.current;
    return () => {
      mounted.current = false;
      activeRequests.forEach(controller => controller.abort());
      activeRequests.clear();
      activeRefreshes.forEach(controller => controller.abort());
      activeRefreshes.clear();
    };
  }, []);

  useEffect(() => {
    // This GET reads the database only. Changing categories never fetches the external site.
    loadPage(board, page);
  }, [board, page, loadPage]);

  useEffect(() => {
    if (!hasCooldown) return undefined;
    // Update only the clock label, never poll HTTP or trigger a refresh on expiry.
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [hasCooldown]);

  const select = (nextBoard, nextPage) => {
    analysis.close();
    setCoverPreview(null);
    setQuery('');
    setNow(Date.now());
    selectedPage.current = { board: nextBoard, page: nextPage };
    setSelection({ board: nextBoard, page: nextPage });
  };

  const handleRefresh = async () => {
    const control = boardControls.current.get(board);
    // Board-level refs cover double clicks and page changes before React commits the next render.
    if (refreshRequests.current.has(board) || control?.status === 'REFRESHING'
      || rankingCooldownSeconds(control?.nextRefreshAt) || rankingCooldownSeconds(control?.localNextRefreshAt)) return;
    const controller = new AbortController();
    refreshRequests.current.set(board, controller);
    abortBoardReads(board);
    setNow(Date.now());
    updateBoard(board, { busy: true, error: '', warning: '', localNextRefreshAt: new Date(Date.now() + 60_000).toISOString() });
    let synced = false;
    try {
      const data = await refreshHongguoRankings(board, page, { signal: controller.signal });
      if (!mounted.current || controller.signal.aborted || refreshRequests.current.get(board) !== controller) return;
      const incoming = normalizeRankingSnapshot(data, board, page);
      abortBoardReads(board);
      synced = incoming.status === 'READY' && !incoming.stale;
      setEntries(current => {
        const next = { snapshot: mergeRankingSnapshot(current[key]?.snapshot, incoming), busy: '', error: '' };
        return synced ? replaceBoardEntries(current, board, key, next) : { ...current, [key]: next };
      });
      updateBoard(board, {
        status: incoming.status, nextRefreshAt: incoming.nextRefreshAt,
        fetchedAt: incoming.fetchedAt || control?.fetchedAt, warning: incoming.warning, error: '',
      });
    } catch (cause) {
      if (mounted.current && !controller.signal.aborted && refreshRequests.current.get(board) === controller) {
        abortBoardReads(board);
        updateBoard(board, { error: `${cause?.message || '榜单同步失败。'} 已保存的数据不会清空。不会自动重新同步；可点击「读取已保存数据」核对结果。` });
      }
    } finally {
      if (refreshRequests.current.get(board) === controller) {
        refreshRequests.current.delete(board);
        if (mounted.current) updateBoard(board, { busy: false });
      }
    }
    // The POST returns its original page. If the user moved, load their selected page from the new saved board.
    const active = selectedPage.current;
    if (synced && mounted.current && active.board === board && active.page !== page) loadPage(active.board, active.page);
  };

  return (
    <section className="hongguo-rankings" aria-labelledby="hongguo-rankings-title">
      <header className="hongguo-rankings-heading">
        <div>
          <h2 id="hongguo-rankings-title"><TrendingUp size={17} style={{ color: ADMIN_ACCENT }} />红果榜单<span className="hongguo-rankings-badge">ADMIN · 测试</span></h2>
          <p>公开榜单的独立查看入口，不受项目过滤影响；查询自己剧集的数据后续单独接入。</p>
        </div>
        <a className="hongguo-rankings-source" href={snapshot?.sourceUrl || rankingSourceUrl(board)} target="_blank" rel="noopener noreferrer">查看红果原榜单<ArrowUpRight size={14} /></a>
      </header>

      <div className="hongguo-rankings-panel">
        <div className="hongguo-rankings-toolbar">
          <div className="hongguo-rankings-boards" role="group" aria-label="榜单分类">
            {HONGGUO_BOARDS.map(item => <button key={item.key} type="button" aria-pressed={board === item.key} onClick={() => select(item.key, 1)}>{item.label}</button>)}
          </div>
          <div className="hongguo-rankings-actions">
            <SecondaryButton disabled={Boolean(entry?.busy) || Boolean(boardState?.busy)} onClick={() => loadPage(board, page)} className="hongguo-rankings-action"><Database size={13} />{entry?.busy === 'read' ? '读取中…' : '读取已保存数据'}</SecondaryButton>
            <PrimaryButton disabled={Boolean(entry?.busy) || refreshing || cooldown > 0} onClick={handleRefresh} className="hongguo-rankings-action"><RefreshCw size={13} />{refreshing ? '同步全部 5 页中…' : cooldown > 0 ? `${cooldown} 秒后可同步` : '同步全部 5 页'}</PrimaryButton>
          </div>
        </div>

        <div className="hongguo-rankings-meta" aria-live="polite">
          <div><span>数据来源</span><strong>红果官网公开榜单</strong></div>
          <div><span>原榜更新时间</span><strong>{snapshot?.sourceUpdatedText || '未提供'}</strong></div>
          <div><span>最近成功同步</span><strong>{rankingTime(snapshot?.fetchedAt)}</strong></div>
          <div className="hongguo-rankings-status"><StatusBadge label={refreshing ? '整榜同步中' : status ? STATUS_LABELS[status] : entry?.busy ? '读取中' : '尚未读取'} color={status === 'FAILED' || error ? 'var(--color-error)' : status === 'READY' && !snapshot?.stale ? 'var(--color-success)' : ADMIN_ACCENT} />{snapshot?.stale ? <span>保留上次数据 · 待同步更新</span> : null}</div>
        </div>

        <div className="hongguo-rankings-content">
          {error ? <div role="alert"><ErrorNotice message={error} /></div> : null}
          {boardState?.warning ? <p className="hongguo-rankings-notice" role="status">{boardState.warning}</p> : null}
          {status === 'REFRESHING' && !boardState?.busy ? <p className="hongguo-rankings-notice">服务端正在同步当前榜单全部 5 页。请稍后手动点击「读取已保存数据」查看结果，此页不会自动轮询。</p> : null}
          <div className="hongguo-rankings-table-heading">
            <div><h3>{boardLabel}<span>第 {page} 页</span></h3><p>当前页 {items.length} 条已保存数据 · 一次同步当前榜单全部 5 页，刷新页面仍保留。</p></div>
            <label className="hongguo-rankings-search"><span className="sr-only">搜索当前页</span><Search size={14} aria-hidden="true" /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索当前页剧名 / ID" autoComplete="off" /></label>
          </div>

          {items.length ? <div className="hongguo-rankings-scroll" tabIndex={0} role="region" aria-label={`${boardLabel}第 ${page} 页数据表，可横向滚动`}>
            <table className="hongguo-rankings-table">
              <caption className="sr-only">{boardLabel}第 {page} 页：排名、剧名、热度、评分、收藏数与点赞数。缺失值以破折号表示。</caption>
              <thead><tr><th scope="col">排名</th><th scope="col">剧名</th><th scope="col">热度</th><th scope="col">评分</th><th scope="col">收藏</th><th scope="col">点赞</th><th scope="col">分析</th></tr></thead>
              <tbody>{visibleItems.map((row, index) => <tr key={`${row.seriesId}-${row.rank}-${index}`}>
                <td><span className={`hongguo-rankings-rank${row.rank && row.rank <= 3 ? ' is-top' : ''}`}>{row.rank || '—'}</span></td>
                <th scope="row"><div className="hongguo-rankings-drama">
                  <HongguoRankingCover key={row.coverUrl || 'missing'} url={row.coverUrl} title={row.title} onPreview={() => { analysis.close(); setCoverPreview(row); }} />
                  <div className="hongguo-rankings-drama-info"><div className="hongguo-rankings-title">{row.detailUrl ? <a href={row.detailUrl} target="_blank" rel="noopener noreferrer">{row.title}<ArrowUpRight size={12} /></a> : row.title}</div><div className="hongguo-rankings-row-meta">{row.tags.map((tag, tagIndex) => <span key={tagIndex}>{tag}</span>)}</div><div className="hongguo-rankings-id">ID {row.seriesId || '未提供'}</div></div>
                </div></th>
                <td className="hongguo-rankings-heat">{rankingMetric(row.heatText)}</td><td>{rankingMetric(row.scoreText)}</td><td>{rankingMetric(row.favoriteText)}</td><td>{rankingMetric(row.likeText)}</td>
                <td><button type="button" className="hongguo-rankings-analysis-button" disabled={!/^\d{1,30}$/.test(row.seriesId)} aria-label={`爆款分析：${row.title}`} onClick={() => { setCoverPreview(null); analysis.open({ board, page, seriesId: row.seriesId, title: row.title }); }}><BookOpen size={13} aria-hidden="true" />爆款分析</button></td>
              </tr>)}</tbody>
            </table>
            {!visibleItems.length ? <p className="hongguo-rankings-no-match">当前页没有匹配的剧集。搜索不会查询其他页或未上榜剧集。</p> : null}
          </div> : <div className="hongguo-rankings-empty"><EmptyBlock text={entry?.busy === 'read' ? '正在读取已保存数据…' : refreshing ? '正在同步当前榜单全部 5 页…' : error || status === 'FAILED' ? '暂时没有可用的榜单数据' : '当前榜单此页尚无已保存数据'} hint="点击「同步全部 5 页」抓取并保存当前榜单；切换分类、翻页或刷新页面仅读取已保存数据。" /></div>}

          <footer className="hongguo-rankings-pagination">
            <span>每页最多 20 条 · 仅覆盖上榜剧集</span>
            <nav aria-label="榜单分页"><button type="button" disabled={page <= 1} onClick={() => select(board, page - 1)} aria-label="上一页"><ChevronLeft size={16} /></button>{Array.from({ length: totalPages }, (_, index) => index + 1).map(number => <button key={number} type="button" aria-label={`第 ${number} 页`} aria-current={page === number ? 'page' : undefined} onClick={() => select(board, number)}>{number}</button>)}<button type="button" disabled={page >= totalPages} onClick={() => select(board, page + 1)} aria-label="下一页"><ChevronRight size={16} /></button></nav>
          </footer>
        </div>
      </div>

      {coverPreview ? <Modal isOpen onClose={() => setCoverPreview(null)} title={coverPreview.title} width="max-w-lg" className="hongguo-rankings-cover-modal">
        <HongguoRankingCover key={coverPreview.coverUrl} url={coverPreview.coverUrl} title={coverPreview.title} preview />
      </Modal> : null}
      <HongguoAnalysisModal entry={analysisState.entry} onClose={analysis.close} onCheck={analysis.check} onReanalyze={analysis.reanalyze} />
    </section>
  );
};

export default HongguoRankingsTab;
